import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Select,
  Row,
  Col,
  Tag,
  Card,
  Spin,
  Flex,
  Badge,
} from "antd";
import dayjs from "dayjs";
import { getAllActiveUsers } from "../../../apis/userManagementService";

interface Props {
  open: boolean;
  onClose: () => void;
  availableHospitals: { label: string; value: string | number }[];
  selectedHospital: number | null;
  isReconFileViewMoreModalDataLoading: boolean;
  viewMoreModalData: any[];
  fetchReconFileViewMoreModalData: (payload: any) => void;
  viewMoreDataTotal: number;
}

const ReconFileViewMoreModal: React.FC<Props> = ({
  open,
  onClose,
  availableHospitals,
  selectedHospital,
  isReconFileViewMoreModalDataLoading,
  viewMoreModalData,
  fetchReconFileViewMoreModalData,
  viewMoreDataTotal,
}) => {
  const [modalSelectedHospital, setModalSelectedHospital] = useState<
    string | number
  >(selectedHospital || "");
  const [selectedRegisterType, setSelectedRegisterType] = useState<any>("ALL");
  const [selectedUpdatedBy, setSelectedUpdatedBy] = useState<any>(-1);
  const [selectedDateRangeType, setSelectedDateRangeType] =
    useState<any>("last30Days");
  const [selectedStatus, setSelectedStatus] = useState<any>("ALL");
  const [selectedProcess, setSelectedProcess] = useState<any>("ALL");
  const [users, setUsers] = useState<any>([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    if (!open) {
      setPagination({
        current: 1,
        pageSize: 10,
      });

      setSelectedRegisterType("ALL");
      setSelectedUpdatedBy(-1);
      setSelectedDateRangeType("last30Days");
      setSelectedStatus("ALL");
      setSelectedProcess("ALL");
    }

    if (open) {
      setModalSelectedHospital(selectedHospital || '');
      fetchAllActiveUsers();
      getModalData(true);
    }
  }, [open]);

  const fetchAllActiveUsers = async () => {
    try {
      const resp = await getAllActiveUsers("");
      setUsers(resp);
    } catch (error) {
      console.error("Error fetching active users:", error);
      setUsers([]);
    }
  };

  const calculateDateRange = (type: string) => {
    let startDate;
    let endDate;

    switch (type) {
      case "today":
        startDate = dayjs().startOf("day");
        endDate = dayjs().endOf("day");
        break;

      case "last7Days":
        startDate = dayjs().subtract(6, "day").startOf("day"); // including today
        endDate = dayjs().endOf("day");
        break;

      case "last30Days":
        startDate = dayjs().subtract(29, "day").startOf("day"); // including today
        endDate = dayjs().endOf("day");
        break;

      default:
        startDate = null;
        endDate = null;
    }

    return {
      startDate: startDate ? startDate.format("YYYY-MM-DDTHH:mm:ss.SSS") : null,
      endDate: endDate ? endDate.format("YYYY-MM-DDTHH:mm:ss.SSS") : null,
    };
  };

  useEffect(() => {
    if (open) {
      getModalData(false);
    }
  }, [
    modalSelectedHospital,
    selectedRegisterType,
    selectedUpdatedBy,
    selectedDateRangeType,
    selectedStatus,
    selectedProcess,
  ]);

  const getModalData = (
    isInitialApiCall: boolean,
    page: number = 1,
    size: number = 10,
  ) => {
    const { startDate, endDate } = calculateDateRange(
      isInitialApiCall ? "last30Days" : selectedDateRangeType,
    );
    fetchReconFileViewMoreModalData({
      hospitalId: isInitialApiCall ? selectedHospital : modalSelectedHospital,
      eodBodProcessType: isInitialApiCall ? "ALL" : selectedProcess,
      status: isInitialApiCall ? "ALL" : selectedStatus,
      performedBy: isInitialApiCall ? -1 : selectedUpdatedBy,
      startDate,
      endDate,
      registerType: isInitialApiCall ? "ALL" : selectedRegisterType,
      page: page - 1,
      size,
    });
  };

  const onPaginationChange = (page: number, size: number) => {
    getModalData(false, page, size);
  };

  const handleHospitalChange = (value: string | number) => {
    setModalSelectedHospital(value);
  };

  const handleRegisterTypeChange = (value: string | number) => {
    setSelectedRegisterType(value);
  };

  const handleUpdatedByChange = (id: number) => {
    setSelectedUpdatedBy(id);
  };

  const handleDateChange = (value: string | number) => {
    setSelectedDateRangeType(value);
  };

  const handleStatusChange = (value: string | number) => {
    setSelectedStatus(value);
  };

  const handleProcessChange = (value: string | number) => {
    setSelectedProcess(value);
  };

  const renderValue = (value: any) =>
    value === null || value === undefined || value === "" ? "-" : value;

  const columns = [
    { title: "Process Type", dataIndex: "eodBodProcessType", render: renderValue },
    {
      title: "Hospital Name",
      dataIndex: "hospitalName",
      render: renderValue,
    },
    {
      title: "Register Type",
      dataIndex: "fileType",
      render: renderValue,
    },
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      render: renderValue,
    },
    // { title: "Timestamp", dataIndex: "executedAt", render: renderValue },
    {
      title: "Timestamp",
      dataIndex: "dateAndTime",
      render: (_: any, record: any) => {
        return record?.dateAndTime
          ? dayjs(record.dateAndTime).format("DD-MM-YYYY hh:mm A")
          : "-";
      },
    },
    {
      title: "Status",
      dataIndex: "fileStatus",
      render: (status: string) => (
        // <Tag color={status?.toLowerCase() === "success" ? "green" : "orange"}>
        //   {status ?? "-"}
        // </Tag>
        <Tag
          color={status?.toLowerCase() === "completed" ? "green" : "orange"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            borderRadius: "999px",
            padding: "2px 12px",
          }}
        >
          <Badge
            color={status?.toLowerCase() === "completed" ? "green" : "orange"}
          />
          {status ?? "-"}
        </Tag>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => {
        return (
          <Flex gap={8}>
            <span
              style={{ cursor: "pointer", color: "#00549D" }}
              onClick={() =>
                window.open(
                  `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
                    record?.s3Url,
                  )}`,
                  "_blank",
                )
              }
            >
              View Registers
            </span>
          </Flex>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1550}
      title="Process History :"
    >
      <Card
        size="small"
        style={{
          marginBottom: 16,
          background: "#fafafa",
          border: "1px solid #e5e5e5",
          padding: 12,
        }}
      >
        <Row gutter={16}>
          <Col span={4}>
            <strong>Hospital</strong>
            <Select
              showSearch
              placeholder="Select a Hospital"
              optionFilterProp="label"
              style={{ width: "100%", marginTop: 5 }}
              value={modalSelectedHospital}
              options={availableHospitals}
              onChange={handleHospitalChange}
            />
          </Col>

          <Col span={4}>
            <strong>Register Type</strong>
            <Select
              value={selectedRegisterType}
              style={{ width: "100%", marginTop: 5 }}
              options={[
                { label: "ALL", value: "ALL" },
                // { label: "Credit Register", value: "OP_REGISTER" },
                // { label: "OP Register", value: "OP_REGISTER" },
                // { label: "Discharge Register", value: "DISCHARGE_REGISTER" },
                // { label: "Admission Register", value: "IP_REGISTER" },
              ]}
              onChange={handleRegisterTypeChange}
            />
          </Col>

          <Col span={4}>
            <strong>Updated By</strong>
            <Select
              showSearch
              optionFilterProp="label"
              value={selectedUpdatedBy}
              style={{ width: "100%", marginTop: 5 }}
              options={
                users?.length
                  ? [
                      { label: "ALL", value: -1 },
                      ...users.map((user: any) => ({
                        label: `${user.firstname} ${user.lastname}`,
                        value: user.id,
                      })),
                    ]
                  : [{ label: "ALL", value: -1 }]
              }
              onChange={handleUpdatedByChange}
              allowClear
            />
          </Col>

          <Col span={4}>
            <strong>Date Range</strong>
            <Select
              value={selectedDateRangeType}
              style={{ width: "100%", marginTop: 5 }}
              options={[
                { label: "Last 30 Days", value: "last30Days" },
                { label: "Last 7 Days", value: "last7Days" },
                { label: "Today", value: "today" },
              ]}
              onChange={handleDateChange}
            />
          </Col>

          <Col span={4}>
            <strong>Status</strong>
            <Select
              value={selectedStatus}
              placeholder="Select"
              style={{ width: "100%", marginTop: 5 }}
              options={[
                { label: "ALL", value: "ALL" },
                { label: "PROCESSING", value: "PROCESSING " },
                { label: "COMPLETED", value: "COMPLETED" },
                { label: "FAILED", value: "FAILED" },
              ]}
              onChange={handleStatusChange}
            />
          </Col>

          <Col span={4}>
            <strong>Process</strong>
            <Select
              value={selectedProcess}
              style={{ width: "100%", marginTop: 5 }}
              options={[
                { label: "ALL", value: "ALL" },
                { label: "EOD", value: "EOD" },
                { label: "BOD", value: "BOD" },
              ]}
              onChange={handleProcessChange}
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <Spin spinning={isReconFileViewMoreModalDataLoading}>
          <Table
            columns={columns}
            dataSource={viewMoreModalData}
            rowKey={(record: any, index) => record?.id || index}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: viewMoreDataTotal,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
                const payload = {
                  hospitalId: modalSelectedHospital,
                  fetchType: selectedProcess,
                  status: selectedStatus,
                  performedBy: selectedUpdatedBy,
                  page,
                  size: pageSize,
                };
                onPaginationChange(page, pageSize);
              },
            }}
            scroll={{ y: 400 }}
            size="middle"
            style={{ maxHeight: "500px" }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? "ant-table-row-light" : ""
            }
          />
        </Spin>
      </Card>
    </Modal>
  );
};

export default ReconFileViewMoreModal;
